import React, { useEffect, useState, useCallback } from 'react';
import CustomDateRange from '../../../../../dashboard/components/customDateRange';
import reportPdfIcon from '../../../../../../assets/image/report-pdf-icon.svg';
import arrowBackIcon from '../../../../../../assets/image/subscription/arrow-back-box-icon.svg';
import arrowForwardIcon from '../../../../../../assets/image/subscription/arrow-forward-box-icon.svg';
import { axiosInstanceV2 } from '../../../../../../services/axios';

const columns = [
	{ label: 'DATE', key: 'date', align: 'left' },
	{ label: 'INVOICE ID', key: 'invoice_id', align: 'left' },
	{ label: 'AMOUNT', key: 'amount', align: 'right' },
	{ label: 'STATUS', key: 'status', align: 'center' },
	{ label: 'PAID VIA', key: 'payment_id', align: 'right' },
	{ label: 'VIEW INVOICE', key: 'pdf_url', align: 'center' },
];

const formatDate = (date) =>
	date
		? new Date(date).toLocaleDateString('en-IN', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
			})
		: '-';
const formatAmount = (amt) => (amt != null ? `₹${Number(amt).toFixed(2)}` : '-');
const formatStatus = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '-');

export default function ManageBillingTable() {
	const [invoices, setInvoices] = useState([]);
	const [pagination, setPagination] = useState({
		page: 1,
		page_size: 10,
		total: 0,
		total_pages: 0,
		has_next: false,
		has_prev: false,
	});
	const [loading, setLoading] = useState(false);
	const [params, setParams] = useState({
		start_date: null,
		end_date: null,
		page: 1,
		page_size: 10,
	});

	const fetchInvoices = useCallback(async () => {
		try {
			setLoading(true);
			const token = localStorage.getItem('authToken');

			// build query string
			const query = new URLSearchParams();
			query.set('page', params.page);
			query.set('page_size', params.page_size);
			if (params.start_date) query.set('start_date', params.start_date);
			if (params.end_date) query.set('end_date', params.end_date);

			const response = await axiosInstanceV2.get(`/razorpay/invoices?${query.toString()}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			const data = response.data.data;
			setInvoices(data.invoices ?? []);
			setPagination(data.pagination ?? {});
		} catch (error) {
			console.error('Failed to fetch invoices:', error);
			setInvoices([]);
		} finally {
			setLoading(false);
		}
	}, [params]);

	// fetch on params change
	useEffect(() => {
		fetchInvoices();
	}, [fetchInvoices]);

	const handlePrev = () => {
		if (pagination.has_prev) {
			setParams((p) => ({ ...p, page: p.page - 1 }));
		}
	};

	const handleNext = () => {
		if (pagination.has_next) {
			setParams((p) => ({ ...p, page: p.page + 1 }));
		}
	};

	const handleDateRange = (range) => {
		setParams((p) => ({
			...p,
			start_date: range.start_date,
			end_date: range.end_date,
			page: 1, // reset to page 1 on filter change
		}));
	};

	const renderCell = (invoice, col) => {
		switch (col.key) {
			case 'date':
				return formatDate(invoice.date);

			case 'invoice_id':
				return invoice.invoice_id ?? '-';

			case 'amount':
				return formatAmount(invoice.amount);

			case 'status':
				return (
					<span className={`badge-status badge-${invoice.status}`}>
						{formatStatus(invoice.status)}
					</span>
				);

			case 'payment_id':
				return invoice.paid_via?.display ?? '-';

			case 'pdf_url':
				if (invoice.pdf_url) {
					return (
						<a
							href={invoice.pdf_url}
							target="_blank"
							rel="noreferrer"
							className="d-flex align-items-center justify-content-center gap-2"
							title="Download Invoice"
						>
							<img
								src={reportPdfIcon}
								alt="PDF"
								width={20}
								height={20}
							/>
							<span className="fs-4 fw-light">PDF</span>
						</a>
					);
				}
				// FREE plan rows never get an invoice — "Pending" would wrongly
				// imply one is on its way.
				return <span className="text-muted fs-5">{invoice.is_free_plan ? '-' : 'Pending'}</span>;

			default:
				return '-';
		}
	};

	// pagination display
	const startItem = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
	const endItem = Math.min(pagination.page * pagination.page_size, pagination.total);

	return (
		<div
			className="mt-4"
			style={{ height: '100vh' }}
		>
			{/* ── Header ───────────────────────────────────────────────── */}
			<div
				className="d-flex justify-content-between align-items-center rounded-2 py-3 px-4"
				style={{ backgroundColor: 'rgba(249, 249, 251, 1)' }}
			>
				<div className="fs-4 fw-bold text-dark">Past Transactions</div>

				<div className="d-flex align-items-center gap-4 px-3">
					<CustomDateRange
						handleDataByDateRange={handleDateRange}
						style={{ border: 'none' }}
						range={{ start_date: params.start_date, end_date: params.end_date }}
						setRange={setParams}
					/>

					<div className="fs-4 fw-bold text-dark">
						{pagination.total === 0
							? '0 results'
							: `${startItem} - ${endItem} of ${pagination.total}`}
					</div>

					<img
						width={30}
						height={30}
						className={`cursor-pointer ${!pagination.has_prev ? 'opacity-25' : ''}`}
						src={arrowBackIcon}
						alt="Previous"
						onClick={handlePrev}
					/>
					<img
						width={30}
						height={30}
						className={`cursor-pointer ${!pagination.has_next ? 'opacity-25' : ''}`}
						src={arrowForwardIcon}
						alt="Next"
						onClick={handleNext}
					/>
				</div>
			</div>

			{/* ── Table ────────────────────────────────────────────────── */}
			<div className="report-table-container">
				{loading ? (
					<div className="d-flex justify-content-center align-items-center py-5">
						<span>Loading...</span>
					</div>
				) : invoices.length === 0 ? (
					<div className="d-flex justify-content-center align-items-center py-5 text-muted">
						No transactions found
					</div>
				) : (
					<table
						className="report-table"
						style={{ maxHeight: 'calc(100vh - 500px)' }}
					>
						<thead>
							<tr>
								{columns.map((col, i) => (
									<th key={i}>
										<div
											className={`d-flex align-items-center gap-4 ${
												col.align === 'right'
													? 'justify-content-end'
													: col.align === 'center'
														? 'justify-content-center'
														: 'justify-content-start'
											}`}
										>
											{col.label}
										</div>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{invoices.map((invoice, i) => (
								<tr key={invoice.invoice_id ?? i}>
									{columns.map((col, idx) => (
										<td key={`${i}-${idx}`}>
											<div
												className={`d-flex align-items-center gap-4 ${
													col.align === 'right'
														? 'justify-content-end'
														: col.align === 'center'
															? 'justify-content-center'
															: 'justify-content-start'
												}`}
											>
												{renderCell(invoice, col)}
											</div>
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
}
